import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "@/App.css";
import SEO from "../components/shared/SEO";
import { 
  ArrowLeft,
  BookOpen,
  DollarSign,
  Target,
  TrendingUp,
  Users,
  CheckCircle2,
  Clock,
  Calculator,
  AlertTriangle,
  BarChart3,
  UserPlus,
  CheckSquare,
  RotateCcw,
  Info,
  Lightbulb,
  Calendar,
  ChevronUp
} from "lucide-react";

// Brand Colors
const BRAND = {
  gold: "#D4AF37",
  goldLight: "#E8C547",
  charcoal: "#1C1C1C",
  surface: "#0A0A0A",
  text: "#FAFAFA"
};

// Section Data
const SECTIONS = [
  {
    id: "problem",
    title: "The Problem",
    shortTitle: "Problem",
    icon: AlertTriangle,
    content: [
      {
        type: "lead",
        text: "Most organizations measure SDR unit economics using activity metrics that fail to capture true gross profit contribution."
      },
      {
        type: "metrics3",
        items: [
          { metric: "Meetings Booked", icon: "📅" },
          { metric: "Opportunities Created", icon: "🎯" },
          { metric: "Pipeline Generated", icon: "📊" }
        ]
      },
      {
        type: "warning",
        title: "The Problem",
        text: "These metrics track activity, not gross profit recovery. High pipeline does not guarantee conversion, revenue, or profitable customer acquisition."
      },
      {
        type: "insight",
        title: "The Shift",
        text: "Measure SDR unit economics by customers acquired and gross profit contributed—not meetings booked."
      }
    ]
  },
  {
    id: "cost",
    title: "Fully Loaded Cost",
    shortTitle: "Cost",
    icon: DollarSign,
    content: [
      {
        type: "lead",
        text: "Before measuring performance, organizations must understand the true cost of an SDR. Without this baseline, ROI calculations are incomplete."
      },
      { type: "costBreakdown" }
    ]
  },
  {
    id: "metrics",
    title: "Metrics That Matter",
    shortTitle: "Metrics",
    icon: Target,
    content: [
      {
        type: "lead",
        text: "High-performing organizations shift from activity-based measurement to unit economics measurement. Two metrics define SDR-sourced customer economics."
      },
      { type: "twoMetrics" },
      {
        type: "insight",
        title: "Key Principle",
        text: "Payback measures speed. LTV measures magnitude. High-performing SDR systems optimize both."
      }
    ]
  },
  {
    id: "payback",
    title: "Payback Period",
    shortTitle: "Payback",
    icon: Clock,
    content: [
      {
        type: "lead",
        text: "Payback measures how long it takes to recover SDR acquisition cost from gross profit—not revenue."
      },
      {
        type: "formula",
        title: "SDR-Sourced Payback Formula",
        formula: "Fully Loaded SDR Cost ÷ Monthly Gross Profit from SDR-Sourced Customers",
        description: "Payback answers: 'How many months until gross profit from SDR-sourced deals recovers the SDR program investment?'"
      },
      {
        type: "example",
        text: "SDR cost: $130K/year. SDR sources $500K ARR at 70% margin = $350K gross profit/year = ~$29K/month gross profit recovery. Payback ≈ 4.5 months."
      },
      { type: "paybackBenchmark" }
    ]
  },
  {
    id: "ltv",
    title: "Lifetime Value",
    shortTitle: "LTV",
    icon: TrendingUp,
    content: [
      {
        type: "lead",
        text: "LTV reflects actual gross profit contribution over customer lifetime—not top-line revenue."
      },
      {
        type: "formula",
        title: "Gross-Margin Adjusted LTV",
        formula: "LTV = ARR × Gross Margin % × Avg Customer Lifetime (years)",
        description: "Use gross margin, not revenue. LTV is highly sensitive to churn—interpret as directional, not exact."
      },
      {
        type: "example",
        text: "$50K ARR × 70% gross margin × 4 years = $140K gross-margin adjusted LTV per customer."
      },
      { type: "ltvPrinciple" },
      { type: "unitEconomics" }
    ]
  },
  {
    id: "tenure",
    title: "Tenure Impact",
    shortTitle: "Tenure",
    icon: Users,
    content: [
      {
        type: "lead",
        text: "Tenure is one of the most overlooked drivers of SDR unit economics. Short tenure compresses the productive phase of the investment cycle."
      },
      { type: "tenureImpact" },
      { type: "promotionAdvantage" }
    ]
  },
  {
    id: "checklist",
    title: "Executive Checklist",
    shortTitle: "Checklist",
    icon: CheckSquare,
    content: [
      {
        type: "lead",
        text: "To properly measure SDR-sourced unit economics, leaders should focus on these key actions."
      },
      {
        type: "checklistItems",
        items: [
          "Measure SDR contribution using gross profit from closed customers, not pipeline",
          "Track payback period using monthly gross profit recovery (not annual revenue)",
          "Use gross-margin adjusted LTV for long-term value, not short-term justification",
          "Monitor tenure and ramp dynamics as unit economics multipliers",
          "Compare internal vs external hire unit economics performance",
          "Align SDR metrics to customer acquisition, not activity volume"
        ]
      },
      { type: "bottomLine" }
    ]
  },
  {
    id: "calculator",
    title: "Calculator",
    shortTitle: "Calc",
    icon: Calculator,
    content: [
      {
        type: "lead",
        text: "Model how SDR performance affects payback and unit economics with this interactive planning tool."
      },
      { type: "calculatorComponent" }
    ]
  }
];

// Header Component
const Header = () => {
  const navigate = useNavigate();
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <svg width="24" height="24" viewBox="0 0 64 64" fill="none">
              <rect x="6" y="46" width="11" height="14" fill={BRAND.gold} opacity="0.5" rx="2"/>
              <rect x="20" y="36" width="11" height="24" fill={BRAND.gold} opacity="0.65" rx="2"/>
              <rect x="34" y="24" width="11" height="36" fill={BRAND.gold} opacity="0.8" rx="2"/>
              <rect x="48" y="10" width="11" height="50" fill={BRAND.gold} rx="2"/>
            </svg>
            <span className="text-sm font-bold text-white">LRS</span>
          </button>
          
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-zinc-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </button>
        </div>
      </div>
    </header>
  );
};

// Sticky Section Navigation
const StickyNav = ({ sections, activeSection }) => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const y = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="sticky top-14 sm:top-16 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center gap-1 py-2 overflow-x-auto scrollbar-hide">
          {sections.map((section, i) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full whitespace-nowrap transition-all text-xs font-medium ${
                  isActive 
                    ? 'bg-[#D4AF37] text-[#0A0A0A]' 
                    : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{section.shortTitle}</span>
                <span className="sm:hidden">{i + 1}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Content Block Components
const LeadBlock = ({ text }) => (
  <p className="text-lg sm:text-xl text-zinc-200 leading-relaxed font-medium">
    {text}
  </p>
);

const Metrics3Block = ({ items }) => (
  <div className="grid grid-cols-3 gap-3">
    {items.map((item) => (
      <div key={item.metric} className="p-4 bg-[#1C1C1C] rounded-xl border border-white/5 text-center">
        <div className="text-2xl mb-2">{item.icon}</div>
        <div className="text-white font-medium text-xs sm:text-sm">{item.metric}</div>
      </div>
    ))}
  </div>
);

const WarningBlock = ({ title, text }) => (
  <div className="p-4 sm:p-5 bg-red-500/10 border-l-4 border-red-500 rounded-r-xl">
    <h4 className="text-base font-semibold text-white mb-2">{title}</h4>
    <p className="text-sm text-zinc-300">{text}</p>
  </div>
);

const InsightBlock = ({ title, text }) => (
  <div className="p-4 sm:p-5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl">
    <div className="flex items-start gap-3">
      <Lightbulb className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
      <div>
        <h4 className="font-semibold text-white mb-1">{title}</h4>
        <p className="text-sm text-zinc-300">{text}</p>
      </div>
    </div>
  </div>
);

const FormulaBlock = ({ title, formula, description }) => (
  <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-xl p-5">
    <div className="text-sm text-[#D4AF37] font-semibold mb-2">{title}</div>
    <div className="text-lg sm:text-xl font-mono text-white mb-3 break-words">{formula}</div>
    <div className="text-sm text-zinc-400">{description}</div>
  </div>
);

const ExampleBlock = ({ text }) => (
  <div className="p-4 bg-[#0A0A0A] border border-white/10 rounded-xl">
    <p className="text-sm text-zinc-500 mb-1">Illustrative Example:</p>
    <p className="text-zinc-300 text-sm">{text}</p>
  </div>
);

const CostBreakdownBlock = () => (
  <div className="bg-[#1C1C1C] rounded-2xl p-4 sm:p-6 border border-white/5">
    <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Fully Loaded SDR Cost Components</h3>
    <div className="space-y-3">
      {[
        { item: "Base Salary + Variable Comp", pct: 60 },
        { item: "Benefits & Payroll Taxes", pct: 15 },
        { item: "Tools & Data Platforms", pct: 12 },
        { item: "Management Overhead", pct: 8 },
        { item: "Enablement & Training", pct: 5 }
      ].map((cost) => (
        <div key={cost.item} className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex justify-between text-xs sm:text-sm mb-1">
              <span className="text-zinc-300 truncate">{cost.item}</span>
              <span className="text-[#D4AF37] font-semibold ml-2">{cost.pct}%</span>
            </div>
            <div className="bg-[#0A0A0A] rounded-full h-1.5 overflow-hidden">
              <div className="bg-gradient-to-r from-[#D4AF37] to-[#E8C547] h-full rounded-full" style={{ width: `${cost.pct}%` }} />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-4 pt-4 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
      <span className="text-zinc-400 text-sm">Typical Fully Loaded Cost:</span>
      <span className="text-xl font-bold text-[#D4AF37]">$120K - $135K</span>
    </div>
  </div>
);

const TwoMetricsBlock = () => (
  <div className="grid sm:grid-cols-2 gap-4">
    <div className="p-5 bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 rounded-xl">
      <Clock className="w-8 h-8 text-blue-400 mb-3" />
      <h3 className="text-lg font-bold text-white mb-1">Payback Period</h3>
      <p className="text-blue-400 font-semibold text-sm mb-2">Measures Speed</p>
      <p className="text-zinc-400 text-xs">How long it takes to recover SDR acquisition cost from gross profit generated per customer.</p>
    </div>
    <div className="p-5 bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20 rounded-xl">
      <TrendingUp className="w-8 h-8 text-green-400 mb-3" />
      <h3 className="text-lg font-bold text-white mb-1">Gross-Margin LTV</h3>
      <p className="text-green-400 font-semibold text-sm mb-2">Measures Magnitude</p>
      <p className="text-zinc-400 text-xs">Total gross profit generated by SDR-sourced customers over their lifetime.</p>
    </div>
  </div>
);

const PaybackBenchmarkBlock = () => (
  <div className="bg-[#1C1C1C] rounded-2xl p-4 sm:p-6 border border-white/5">
    <h3 className="text-base font-semibold text-white mb-2">Payback Period Benchmarks</h3>
    <p className="text-xs text-zinc-500 mb-4 italic">Common benchmarks—actual ranges vary by ACV, sales cycle, and market conditions.</p>
    <div className="space-y-3">
      {[
        { range: "6-9 mo", label: "Best-in-class", color: "bg-green-500", width: "w-1/4" },
        { range: "9-12 mo", label: "Typical range", color: "bg-[#D4AF37]", width: "w-2/4" },
        { range: "12-18 mo", label: "Risk Zone", color: "bg-orange-500", width: "w-3/4" },
        { range: "18+ mo", label: "Needs Review", color: "bg-red-500", width: "w-full" }
      ].map((item) => (
        <div key={item.label} className="flex items-center gap-3">
          <div className="w-16 text-xs text-zinc-400 flex-shrink-0">{item.range}</div>
          <div className="flex-1 bg-[#0A0A0A] rounded-full h-6 overflow-hidden">
            <div className={`${item.color} ${item.width} h-full rounded-full flex items-center justify-end pr-3`}>
              <span className="text-[9px] font-semibold text-white truncate">{item.label}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const LtvPrincipleBlock = () => (
  <div className="p-5 bg-[#1C1C1C] border border-white/10 rounded-xl">
    <h4 className="text-base font-semibold text-white mb-3">Critical Principle</h4>
    <p className="text-zinc-300 text-sm mb-3">LTV should <span className="text-red-400 font-bold">NOT</span> be used to calculate payback. They measure different things:</p>
    <div className="grid grid-cols-2 gap-3">
      <div className="p-3 bg-blue-500/10 rounded-lg">
        <div className="text-blue-400 font-semibold text-sm mb-1">Payback →</div>
        <div className="text-zinc-400 text-xs">Short-term gross profit recovery (monthly)</div>
      </div>
      <div className="p-3 bg-green-500/10 rounded-lg">
        <div className="text-green-400 font-semibold text-sm mb-1">Gross-Margin LTV →</div>
        <div className="text-zinc-400 text-xs">Total lifetime gross profit creation (years)</div>
      </div>
    </div>
  </div>
);

const UnitEconomicsBlock = () => (
  <div className="bg-[#1C1C1C] rounded-2xl p-5 border border-white/5">
    <h4 className="text-base font-semibold text-white mb-4">The Combined Model</h4>
    <div className="grid sm:grid-cols-2 gap-3">
      <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
        <div className="text-green-400 font-semibold text-sm mb-2">✓ Strong Unit Economics</div>
        <div className="text-zinc-300 text-xs">Fast payback (&lt;12 mo) + High LTV (&gt;3x SDR-sourced CAC)</div>
      </div>
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
        <div className="text-red-400 font-semibold text-sm mb-2">✗ Weak Unit Economics</div>
        <div className="text-zinc-300 text-xs">Slow payback (&gt;18 mo) + Low LTV (&lt;2x SDR-sourced CAC)</div>
      </div>
    </div>
  </div>
);

const TenureImpactBlock = () => (
  <div className="bg-[#1C1C1C] rounded-2xl p-4 sm:p-6 border border-white/5">
    <h3 className="text-base font-semibold text-white mb-4">Impact of Tenure on Productive Output</h3>
    <div className="grid grid-cols-2 gap-4">
      <div className="text-center">
        <div className="text-2xl font-bold text-[#D4AF37] mb-1">18 mo</div>
        <div className="text-xs text-zinc-400 mb-3">Avg Tenure</div>
        <div className="bg-[#0A0A0A] rounded-xl p-3">
          <div className="flex gap-0.5 justify-center mb-2">
            <div className="w-3 h-10 bg-orange-500/50 rounded"></div>
            <div className="w-3 h-10 bg-green-500 rounded"></div>
            <div className="w-3 h-10 bg-green-500 rounded"></div>
            <div className="w-3 h-10 bg-green-500 rounded"></div>
            <div className="w-3 h-10 bg-green-500 rounded"></div>
            <div className="w-3 h-10 bg-green-500 rounded"></div>
          </div>
          <div className="text-[10px] text-zinc-400">3mo + <span className="text-green-400 font-semibold">15mo</span></div>
        </div>
      </div>
      <div className="text-center">
        <div className="text-2xl font-bold text-zinc-500 mb-1">12 mo</div>
        <div className="text-xs text-zinc-400 mb-3">Reduced</div>
        <div className="bg-[#0A0A0A] rounded-xl p-3">
          <div className="flex gap-0.5 justify-center mb-2">
            <div className="w-3 h-10 bg-orange-500/50 rounded"></div>
            <div className="w-3 h-10 bg-green-500 rounded"></div>
            <div className="w-3 h-10 bg-green-500 rounded"></div>
            <div className="w-3 h-10 bg-green-500 rounded"></div>
            <div className="w-3 h-10 bg-zinc-700 rounded opacity-30"></div>
            <div className="w-3 h-10 bg-zinc-700 rounded opacity-30"></div>
          </div>
          <div className="text-[10px] text-zinc-400">3mo + <span className="text-green-400 font-semibold">9mo</span></div>
        </div>
      </div>
    </div>
    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-center">
      <span className="text-red-400 font-bold">~40% reduction</span>
      <span className="text-zinc-400 text-sm ml-2">in productive output when tenure drops from 18 to 12 months</span>
    </div>
  </div>
);

const PromotionAdvantageBlock = () => (
  <div className="p-5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl">
    <div className="flex items-start gap-3">
      <UserPlus className="w-6 h-6 text-[#D4AF37] flex-shrink-0" />
      <div>
        <h4 className="text-base font-semibold text-white mb-2">Internal Promotion as a Multiplier</h4>
        <p className="text-zinc-400 text-sm mb-3">Research shows internally promoted employees reach productivity 20–30% faster and demonstrate higher retention.</p>
        <ul className="space-y-1.5">
          {["Ramp time decreases", "Hiring risk decreases", "Performance consistency improves", "Reduce dependency on external hiring"].map((item) => (
            <li key={item} className="flex items-center gap-2 text-zinc-300 text-sm">
              <CheckCircle2 className="w-3.5 h-3.5 text-[#D4AF37]" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const ChecklistItemsBlock = ({ items }) => (
  <div className="space-y-3">
    {items.map((item, i) => (
      <div key={item} className="flex items-center gap-3 p-3 bg-[#1C1C1C] rounded-xl border border-white/5">
        <div className="w-7 h-7 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
          <span className="text-[#0A0A0A] font-bold text-xs">{i + 1}</span>
        </div>
        <span className="text-white text-sm">{item}</span>
      </div>
    ))}
  </div>
);

const BottomLineBlock = () => (
  <div className="p-5 bg-gradient-to-r from-[#1C1C1C] to-[#0A0A0A] rounded-2xl border border-[#D4AF37]/30">
    <h3 className="text-lg font-bold text-white mb-3 text-center">Bottom Line</h3>
    <p className="text-base text-zinc-300 text-center mb-4">
      SDR teams are not activity engines. <span className="text-[#D4AF37] font-semibold">They are customer acquisition systems.</span>
    </p>
    <div className="grid grid-cols-2 gap-4">
      <div className="text-center p-3 bg-[#0A0A0A] rounded-xl">
        <Clock className="w-6 h-6 text-blue-400 mx-auto mb-2" />
        <div className="text-white font-semibold text-xs">How fast gross profit recovers acquisition cost</div>
      </div>
      <div className="text-center p-3 bg-[#0A0A0A] rounded-xl">
        <TrendingUp className="w-6 h-6 text-green-400 mx-auto mb-2" />
        <div className="text-white font-semibold text-xs">How much gross-margin adjusted LTV is created</div>
      </div>
    </div>
  </div>
);

// Calculator Components
const CalcInputField = ({ label, field, value, prefix, suffix, hint, onChange, onKeyDown }) => (
  <div className="space-y-1">
    <label className="block text-xs font-medium text-zinc-300">{label}</label>
    {hint && <p className="text-[10px] text-zinc-500">{hint}</p>}
    <div className="relative">
      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none text-sm">{prefix}</span>}
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(field, e.target.value)}
        onKeyDown={onKeyDown}
        className={`w-full bg-[#0A0A0A] border border-white/10 rounded-lg py-2.5 text-white text-sm focus:border-[#D4AF37]/50 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/30 transition-all ${prefix ? 'pl-6' : 'pl-3'} ${suffix ? 'pr-8' : 'pr-3'}`}
        data-testid={`calc-input-${field}`}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none text-sm">{suffix}</span>}
    </div>
  </div>
);

const CalcOutputCard = ({ label, value, subtext, highlight }) => (
  <div className={`p-3 rounded-xl border ${highlight ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30' : 'bg-[#0A0A0A] border-white/10'}`}>
    <div className="text-[10px] text-zinc-500 uppercase tracking-wide mb-0.5">{label}</div>
    <div className={`text-lg font-bold ${highlight ? 'text-[#D4AF37]' : 'text-white'}`}>{value}</div>
    {subtext && <div className="text-[10px] text-zinc-500 mt-0.5">{subtext}</div>}
  </div>
);

// Calculator default values
const CALCULATOR_DEFAULTS = {
  SDR_COST: 130000,
  NUM_SDRS: 2,
  MEETINGS_PER_MONTH: 40,
  MEETING_TO_OPP_RATE: 35,
  OPP_TO_CLOSE_RATE: 25,
  ACV: 50000,
  GROSS_MARGIN: 70,
  CUSTOMER_LIFETIME: 4
};

const SDRCalculator = () => {
  const defaultValues = {
    sdrCost: String(CALCULATOR_DEFAULTS.SDR_COST),
    numSDRs: String(CALCULATOR_DEFAULTS.NUM_SDRS),
    meetingsPerMonth: String(CALCULATOR_DEFAULTS.MEETINGS_PER_MONTH),
    meetingToOppRate: String(CALCULATOR_DEFAULTS.MEETING_TO_OPP_RATE),
    oppToCloseRate: String(CALCULATOR_DEFAULTS.OPP_TO_CLOSE_RATE),
    acv: String(CALCULATOR_DEFAULTS.ACV),
    grossMargin: String(CALCULATOR_DEFAULTS.GROSS_MARGIN),
    customerLifetime: String(CALCULATOR_DEFAULTS.CUSTOMER_LIFETIME)
  };

  const [draftInputs, setDraftInputs] = useState(defaultValues);
  const [committedInputs, setCommittedInputs] = useState({
    sdrCost: CALCULATOR_DEFAULTS.SDR_COST,
    numSDRs: CALCULATOR_DEFAULTS.NUM_SDRS,
    meetingsPerMonth: CALCULATOR_DEFAULTS.MEETINGS_PER_MONTH,
    meetingToOppRate: CALCULATOR_DEFAULTS.MEETING_TO_OPP_RATE,
    oppToCloseRate: CALCULATOR_DEFAULTS.OPP_TO_CLOSE_RATE,
    acv: CALCULATOR_DEFAULTS.ACV,
    grossMargin: CALCULATOR_DEFAULTS.GROSS_MARGIN,
    customerLifetime: CALCULATOR_DEFAULTS.CUSTOMER_LIFETIME
  });
  const [hasChanges, setHasChanges] = useState(false);

  const handleInputChange = (field, value) => {
    const sanitized = value.replace(/[^0-9.]/g, '');
    setDraftInputs(prev => ({ ...prev, [field]: sanitized }));
    setHasChanges(true);
  };

  const handleCalculate = () => {
    const numericInputs = {
      sdrCost: parseFloat(draftInputs.sdrCost) || 0,
      numSDRs: parseFloat(draftInputs.numSDRs) || 0,
      meetingsPerMonth: parseFloat(draftInputs.meetingsPerMonth) || 0,
      meetingToOppRate: parseFloat(draftInputs.meetingToOppRate) || 0,
      oppToCloseRate: parseFloat(draftInputs.oppToCloseRate) || 0,
      acv: parseFloat(draftInputs.acv) || 0,
      grossMargin: parseFloat(draftInputs.grossMargin) || 0,
      customerLifetime: parseFloat(draftInputs.customerLifetime) || 0
    };
    setCommittedInputs(numericInputs);
    setHasChanges(false);
  };

  const handleReset = () => {
    setDraftInputs(defaultValues);
    setCommittedInputs({
      sdrCost: CALCULATOR_DEFAULTS.SDR_COST,
      numSDRs: CALCULATOR_DEFAULTS.NUM_SDRS,
      meetingsPerMonth: CALCULATOR_DEFAULTS.MEETINGS_PER_MONTH,
      meetingToOppRate: CALCULATOR_DEFAULTS.MEETING_TO_OPP_RATE,
      oppToCloseRate: CALCULATOR_DEFAULTS.OPP_TO_CLOSE_RATE,
      acv: CALCULATOR_DEFAULTS.ACV,
      grossMargin: CALCULATOR_DEFAULTS.GROSS_MARGIN,
      customerLifetime: CALCULATOR_DEFAULTS.CUSTOMER_LIFETIME
    });
    setHasChanges(false);
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); handleCalculate(); } };

  const outputs = useMemo(() => {
    const { sdrCost, numSDRs, meetingsPerMonth, meetingToOppRate, oppToCloseRate, acv, grossMargin, customerLifetime } = committedInputs;
    const meetingToOppDecimal = meetingToOppRate / 100;
    const oppToCloseDecimal = oppToCloseRate / 100;
    const grossMarginDecimal = grossMargin / 100;
    const annualMeetings = meetingsPerMonth * 12 * numSDRs;
    const opportunitiesCreated = annualMeetings * meetingToOppDecimal;
    const customersWon = opportunitiesCreated * oppToCloseDecimal;
    const estimatedARR = customersWon * acv;
    const grossMarginAdjustedLTV = acv * grossMarginDecimal * customerLifetime;
    const sdrProgramCost = sdrCost * numSDRs;
    const sdrSourcedCAC = customersWon > 0 ? sdrProgramCost / customersWon : null;
    const monthlyGrossProfit = (acv / 12) * grossMarginDecimal;
    const cacPaybackMonths = (monthlyGrossProfit > 0 && sdrSourcedCAC !== null && sdrSourcedCAC > 0) ? sdrSourcedCAC / monthlyGrossProfit : null;
    const ltvCacRatio = (sdrSourcedCAC !== null && sdrSourcedCAC > 0) ? grossMarginAdjustedLTV / sdrSourcedCAC : null;
    return {
      opportunitiesCreated: Math.round(opportunitiesCreated * 10) / 10,
      customersWon: Math.round(customersWon * 10) / 10,
      estimatedARR, grossMarginAdjustedLTV, sdrProgramCost, sdrSourcedCAC,
      ltvCacRatio: ltvCacRatio !== null ? Math.round(ltvCacRatio * 10) / 10 : null,
      cacPaybackMonths: cacPaybackMonths !== null ? Math.round(cacPaybackMonths * 10) / 10 : null,
      hasValidCustomers: customersWon > 0, hasValidGrossMargin: grossMargin > 0
    };
  }, [committedInputs]);

  const getInterpretation = () => {
    const { ltvCacRatio, cacPaybackMonths, hasValidCustomers, hasValidGrossMargin } = outputs;
    if (!hasValidCustomers) return { ltvCac: { status: 'neutral', message: 'Adjust conversion rates to generate customers.' }, payback: { status: 'neutral', message: 'Adjust conversion rates to generate customers.' } };
    if (!hasValidGrossMargin) return { ltvCac: { status: 'neutral', message: 'Set gross margin above 0%.' }, payback: { status: 'neutral', message: 'Set gross margin above 0%.' } };
    
    // LTV:CAC interpretation
    let ltvCacInterpretation;
    if (ltvCacRatio === null || ltvCacRatio < 1.5) {
      ltvCacInterpretation = { status: 'weak', message: 'Below 1.5x — unit economics are weak.' };
    } else if (ltvCacRatio < 3) {
      ltvCacInterpretation = { status: 'workable', message: '1.5x to 3x — workable but needs improvement.' };
    } else {
      ltvCacInterpretation = { status: 'healthy', message: 'Above 3x — unit economics look healthy.' };
    }
    
    // Payback interpretation
    let paybackInterpretation;
    if (cacPaybackMonths !== null && cacPaybackMonths > 0 && cacPaybackMonths < 12) {
      paybackInterpretation = { status: 'strong', message: 'Under 12 months — strong payback.' };
    } else if (cacPaybackMonths !== null && cacPaybackMonths >= 12 && cacPaybackMonths <= 18) {
      paybackInterpretation = { status: 'workable', message: '12 to 18 months — workable payback.' };
    } else {
      paybackInterpretation = { status: 'long', message: 'Over 18 months — long payback period.' };
    }
    
    return { ltvCac: ltvCacInterpretation, payback: paybackInterpretation };
  };

  const interpretation = getInterpretation();
  
  const formatCurrency = (value) => {
    if (value === null || isNaN(value)) return '—';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${Math.round(value).toLocaleString()}`;
  };
  
  const getStatusColor = (status) => {
    if (status === 'healthy' || status === 'strong') return 'bg-green-500/10 border-green-500/30 text-green-400';
    if (status === 'workable') return 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400';
    if (status === 'weak' || status === 'long') return 'bg-red-500/10 border-red-500/30 text-red-400';
    return 'bg-zinc-500/10 border-zinc-500/30 text-zinc-400';
  };

  return (
    <div className="bg-[#1C1C1C] rounded-2xl border border-white/5 overflow-hidden">
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calculator className="w-5 h-5 text-[#D4AF37]" />
            <h3 className="text-base font-semibold text-white">Interactive SDR Economics Calculator</h3>
          </div>
          <button onClick={handleReset} className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] text-zinc-400 hover:text-[#D4AF37] bg-[#0A0A0A] rounded-lg border border-white/10 hover:border-[#D4AF37]/30 transition-all" data-testid="calc-reset-btn">
            <RotateCcw className="w-3 h-3" />Reset
          </button>
        </div>
      </div>
      
      <div className="grid lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-white/5">
        <div className="p-4">
          <h4 className="text-sm font-semibold text-white mb-3">Inputs</h4>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <CalcInputField label="Cost per SDR" field="sdrCost" value={draftInputs.sdrCost} prefix="$" hint="Fully loaded" onChange={handleInputChange} onKeyDown={handleKeyDown} />
              <CalcInputField label="# of SDRs" field="numSDRs" value={draftInputs.numSDRs} onChange={handleInputChange} onKeyDown={handleKeyDown} />
            </div>
            <CalcInputField label="Meetings/Month (per SDR)" field="meetingsPerMonth" value={draftInputs.meetingsPerMonth} onChange={handleInputChange} onKeyDown={handleKeyDown} />
            <div className="grid grid-cols-2 gap-3">
              <CalcInputField label="Meeting → Opp" field="meetingToOppRate" value={draftInputs.meetingToOppRate} suffix="%" onChange={handleInputChange} onKeyDown={handleKeyDown} />
              <CalcInputField label="Opp → Close" field="oppToCloseRate" value={draftInputs.oppToCloseRate} suffix="%" onChange={handleInputChange} onKeyDown={handleKeyDown} />
            </div>
            <CalcInputField label="ACV" field="acv" value={draftInputs.acv} prefix="$" onChange={handleInputChange} onKeyDown={handleKeyDown} />
            <div className="grid grid-cols-2 gap-3">
              <CalcInputField label="Gross Margin" field="grossMargin" value={draftInputs.grossMargin} suffix="%" onChange={handleInputChange} onKeyDown={handleKeyDown} />
              <CalcInputField label="Customer Lifetime" field="customerLifetime" value={draftInputs.customerLifetime} suffix="yrs" onChange={handleInputChange} onKeyDown={handleKeyDown} />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/10">
            <button onClick={handleCalculate} className={`w-full py-2.5 px-4 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm ${hasChanges ? 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#E8C547]' : 'bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30'}`} data-testid="calc-calculate-btn">
              <Calculator className="w-4 h-4" />{hasChanges ? 'Calculate' : 'Calculated'}
            </button>
          </div>
        </div>

        <div className="p-4 bg-[#0A0A0A]/50">
          <h4 className="text-sm font-semibold text-white mb-3">Outputs <span className="text-[10px] text-zinc-500 font-normal">(Annualized)</span></h4>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <CalcOutputCard label="Opportunities" value={outputs.opportunitiesCreated} subtext="per year" />
              <CalcOutputCard label="Customers Won" value={outputs.customersWon} subtext="per year" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <CalcOutputCard label="Est. ARR" value={formatCurrency(outputs.estimatedARR)} />
              <CalcOutputCard label="GM-Adj LTV" value={formatCurrency(outputs.grossMarginAdjustedLTV)} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <CalcOutputCard label="SDR Program Cost" value={formatCurrency(outputs.sdrProgramCost)} />
              <CalcOutputCard label="SDR-Sourced CAC" value={outputs.hasValidCustomers ? formatCurrency(outputs.sdrSourcedCAC) : '—'} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <CalcOutputCard label="LTV:CAC" value={outputs.ltvCacRatio !== null ? `${outputs.ltvCacRatio}x` : '—'} highlight={outputs.ltvCacRatio !== null && outputs.ltvCacRatio >= 3} />
              <CalcOutputCard label="CAC Payback" value={outputs.cacPaybackMonths !== null ? `${outputs.cacPaybackMonths} mo` : '—'} highlight={outputs.cacPaybackMonths !== null && outputs.cacPaybackMonths > 0 && outputs.cacPaybackMonths < 12} />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 text-xs text-zinc-400"><Info className="w-3 h-3" />Interpretation</div>
            <div className={`p-3 rounded-xl border ${getStatusColor(interpretation.ltvCac.status)}`}>
              <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-3 h-3" /><span className="font-semibold text-xs">LTV:CAC</span></div>
              <p className="text-xs opacity-90">{interpretation.ltvCac.message}</p>
            </div>
            <div className={`p-3 rounded-xl border ${getStatusColor(interpretation.payback.status)}`}>
              <div className="flex items-center gap-2 mb-1"><Clock className="w-3 h-3" /><span className="font-semibold text-xs">Payback</span></div>
              <p className="text-xs opacity-90">{interpretation.payback.message}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="px-4 py-3 bg-[#0A0A0A] border-t border-white/5">
        <p className="text-[10px] text-zinc-500 text-center">Illustrative planning tool. Actual unit economics depend on retention, expansion, and attribution methodology.</p>
      </div>
    </div>
  );
};

// Section Component
const Section = ({ section, index, isVisible }) => {
  const Icon = section.icon;
  
  const renderContent = (content, idx) => {
    switch (content.type) {
      case 'lead': return <LeadBlock key={idx} text={content.text} />;
      case 'metrics3': return <Metrics3Block key={idx} items={content.items} />;
      case 'warning': return <WarningBlock key={idx} title={content.title} text={content.text} />;
      case 'insight': return <InsightBlock key={idx} title={content.title} text={content.text} />;
      case 'formula': return <FormulaBlock key={idx} title={content.title} formula={content.formula} description={content.description} />;
      case 'example': return <ExampleBlock key={idx} text={content.text} />;
      case 'costBreakdown': return <CostBreakdownBlock key={idx} />;
      case 'twoMetrics': return <TwoMetricsBlock key={idx} />;
      case 'paybackBenchmark': return <PaybackBenchmarkBlock key={idx} />;
      case 'ltvPrinciple': return <LtvPrincipleBlock key={idx} />;
      case 'unitEconomics': return <UnitEconomicsBlock key={idx} />;
      case 'tenureImpact': return <TenureImpactBlock key={idx} />;
      case 'promotionAdvantage': return <PromotionAdvantageBlock key={idx} />;
      case 'checklistItems': return <ChecklistItemsBlock key={idx} items={content.items} />;
      case 'bottomLine': return <BottomLineBlock key={idx} />;
      case 'calculatorComponent': return <SDRCalculator key={idx} />;
      default: return null;
    }
  };

  return (
    <section 
      id={section.id}
      className={`transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#D4AF37] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#D4AF37]/20">
          <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#0A0A0A]" />
        </div>
        <div>
          <span className="text-xs font-medium text-[#D4AF37]">Section {index + 1}</span>
          <h2 className="text-xl sm:text-2xl font-bold text-white">{section.title}</h2>
        </div>
      </div>

      <div className="space-y-5 pl-0 sm:pl-[72px]">
        {section.content.map((content, idx) => renderContent(content, idx))}
      </div>

      <div className="mt-10 mb-10 border-t border-white/5" />
    </section>
  );
};

// Back to Top Button
const BackToTop = ({ show }) => (
  <button
    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    className={`fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-[#D4AF37] text-[#0A0A0A] shadow-lg shadow-[#D4AF37]/30 flex items-center justify-center transition-all duration-300 ${
      show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
    }`}
  >
    <ChevronUp className="w-6 h-6" />
  </button>
);

// Main Component
function SDREconomicsPage() {
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);

      const scrollPosition = window.scrollY + 150;
      
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const section = SECTIONS[i];
        const element = document.getElementById(section.id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
    // SECTIONS is a static constant defined outside component
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] overflow-x-hidden">
      <SEO 
        title="SDR Economics | Executive Measurement Playbook"
        description="How high-performing sales and RevOps leaders actually measure SDR ROI. Fully loaded costs, payback periods, LTV calculations, and tenure impact."
        path="/resources/sdr-economics"
        image="resources"
      />
      <Header />
      <StickyNav sections={SECTIONS} activeSection={activeSection} />
      
      {/* Hero */}
      <section className="pt-28 sm:pt-32 pb-8 sm:pb-10 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-transparent" />
          <img 
            src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1920&auto=format&fit=crop"
            alt="Finance"
            className="absolute inset-0 w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]/70" />
        </div>
        
        <div className="relative max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs font-medium text-[#D4AF37]">LRS Playbook</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight mb-4">
            SDR Economics:{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#E8C547]">
              Executive Measurement Playbook
            </span>
          </h1>
          
          <p className="text-base sm:text-lg text-zinc-300 mb-6 max-w-2xl">
            How High-Performing Sales and RevOps Leaders Actually Measure SDR ROI.
          </p>
          
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_efficiency-boost-10/artifacts/tvktt6ia_IMG_0009.jpeg"
              alt="Dan Leitner"
              className="w-12 h-12 rounded-full object-cover border-2 border-[#D4AF37]/30"
            />
            <div>
              <div className="font-medium text-white">Dan Leitner</div>
              <div className="text-sm text-[#D4AF37]">Founder & Principal Consultant</div>
            </div>
          </div>
        </div>
      </section>

      {/* All Sections */}
      <div className="px-4 sm:px-6 lg:px-12 pb-20">
        <div className="max-w-4xl mx-auto">
          {SECTIONS.map((section, index) => (
            <Section
              key={section.id}
              section={section}
              index={index}
              isVisible={isLoaded}
            />
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div className="px-4 sm:px-6 pb-20">
        <div className="max-w-4xl mx-auto p-8 bg-gradient-to-br from-[#D4AF37]/20 to-[#1C1C1C] rounded-2xl border border-[#D4AF37]/30 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">Need Help With Your SDR Economics?</h3>
          <p className="text-zinc-300 mb-6 max-w-md mx-auto">
            Let's discuss how to optimize your SDR unit economics and build systems that scale.
          </p>
          <a 
            href="https://calendly.com/leitner-revenue-systems/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-[#0A0A0A] rounded-xl font-semibold hover:bg-[#E8C547] transition-all shadow-lg shadow-[#D4AF37]/20"
          >
            <Calendar className="w-5 h-5" />
            Book a Strategy Call
          </a>
        </div>
      </div>

      <BackToTop show={showBackToTop} />
    </div>
  );
}

export default SDREconomicsPage;
